import { Card } from '@/app/common/components/Card'
import PageLoader from '@/app/common/components/PageLoader'
import { parseRequestError } from '@/app/common/utilities'
import { currencyFormatter } from '@/lib/utils'
import { useToast } from '@/shad/ui/toast/use-toast'
import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { AlertTriangle, Award, Clock } from 'lucide-react'
import { z } from 'zod'
import type { Purse } from '@/queries/program_purses/schemas.ts'
import { DistributionPlanSchema } from './schema'
import { ScholarshipAwardCard } from './scholarship-award-card'
import { USE_WITH_CAUTION_AWARD_SCHOLARSHIPS } from './USE_WITH_CAUTION.api'

type Props = {
  purses: Purse[]
  programId: number
}

const DistributionStatusSchema = z.union([
  z.literal('pending'),
  z.literal('processing'),
  z.literal('completed'),
  z.literal('failed'),
])
export type DistributionStatus = z.infer<typeof DistributionStatusSchema>

const JobStatusSchema = z.object({
  id: z.number(),
  status: DistributionStatusSchema,
  total_planned_amount: z.string(),
  total_planned_students: z.number(),
  processed_students: z.number(),
  failed_processed_students: z.number(),
  progress_percentage: z.number(),
  started_at: z.string(),
  completed_at: z.string().nullable(),
  error_message: z.string().nullable(),
})

export default function ScholarshipAwarding(props: Props) {
  const { purses, programId } = props
  const { toast } = useToast()
  const awardScholarshipMutation = useMutation({
    mutationFn: async (purseId: number) => {
      const response = await USE_WITH_CAUTION_AWARD_SCHOLARSHIPS({
        program_id: programId,
        program_purse_id: purseId,
      })

      // Safely parse the response using the schema
      // We dont want any errors in our mutations here.
      const parseResult = DistributionPlanSchema.safeParse(response)

      return parseResult
    },
  })

  const distributionStatusQuery = useQuery({
    queryFn: async () => {
      const response = await axios.get(
        '/scholarships/latest_distribution_status',
        {
          params: {
            program_id: programId,
          },
        }
      )
      if (response.data.status === 'No Distribution Found') {
        return null
      }

      return JobStatusSchema.parse(response.data)
    },
    queryKey: ['distribution-status'],
  })

  function handleAwardPurse(purseId: number) {
    awardScholarshipMutation.mutate(purseId, {
      onSuccess: () => {
        // Scroll to top when the process is successful
        window.scrollTo({ top: 0 })

        toast({
          title: 'Scholarship award process started',
          description: 'The scholarship award process has been started.',
        })
      },
      onError: (error) => {
        const parsedError = parseRequestError(error)

        // Check for specific case where another distribution is already running
        if (
          parsedError.details?.exception ===
          'Another distribution process is already running'
        ) {
          toast({
            title: 'Distribution Already in Progress',
            variant: 'destructive',
            description:
              'Another scholarship distribution process is currently running. Please wait for it to complete before starting a new one.',
          })
          return
        }

        toast({
          title: 'Error',
          variant: 'destructive',
          description: parsedError.message,
        })
      },
    })
  }

  if (awardScholarshipMutation.status === 'success') {
    // Failed to parse response but API call was successful - show generic success
    return (
      <Card className="max-w-md mx-auto mt-10">
        <div className="text-center py-12 px-6">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <Award className="w-8 h-8 text-green-600" />
          </div>

          <h3 className="text-2xl font-semibold text-gray-900 mb-2">
            Process Started Successfully!
          </h3>

          <p className="text-gray-600 leading-relaxed">
            Your scholarship award process has been initiated. The system is now
            processing the awards and you should see the results shortly.
          </p>
        </div>
      </Card>
    )
  }

  if (purses.length === 0) {
    return (
      <Card className="max-w-md mx-auto mt-10">
        <div className="text-center py-12 px-6">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
            <Award />
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No purses available
          </h3>

          <p className="text-gray-600 leading-relaxed">
            Scholarships can be awarded to students when you create a purse.
            Start by setting up your first purse to begin the scholarship
            awarding process.
          </p>
        </div>
      </Card>
    )
  }

  return (
    <PageLoader query={distributionStatusQuery}>
      {(data) => {
        // Handle the case where no distribution status is found
        if (!data) {
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 max-w-5xl mx-auto">
              {purses.map((purse) => (
                <ScholarshipAwardCard
                  distributionStatus="pending"
                  awardStatus={awardScholarshipMutation.status}
                  onAward={handleAwardPurse}
                  key={purse.id + purse.name}
                  purse={purse}
                  programId={programId}
                />
              ))}
            </div>
          )
        }

        return (
          <div>
            {data.status === 'processing' && (
              <Card className="max-w-5xl mx-auto mb-8 w-full">
                <div className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-amber-600 animate-pulse" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Distribution In Progress
                        </h3>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          Processing
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-600">
                        A scholarship distribution process is currently running.
                        Please wait for it to complete before starting a new
                        distribution.
                      </p>

                      <div className="mt-4 space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Progress</span>
                          <span className="font-medium text-gray-900">
                            {data.progress_percentage}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-amber-500 h-2 rounded-full transition-all duration-300 ease-in-out"
                            style={{ width: `${data.progress_percentage}%` }}
                          ></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">
                              Students Processed:
                            </span>
                            <span className="ml-1 font-medium text-gray-900">
                              {data.processed_students.toLocaleString()} /{' '}
                              {data.total_planned_students.toLocaleString()}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Total Amount:</span>
                            <span className="ml-1 font-medium text-gray-900">
                              {currencyFormatter.format(
                                parseFloat(data.total_planned_amount)
                              )}
                            </span>
                          </div>
                        </div>
                        {data.failed_processed_students > 0 && (
                          <div className="mt-2 p-3 bg-red-50 rounded-lg">
                            <div className="flex items-center">
                              <AlertTriangle className="w-4 h-4 text-red-500 mr-2" />
                              <span className="text-sm text-red-700">
                                {data.failed_processed_students} student(s)
                                failed to process
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 max-w-5xl mx-auto">
              {purses.map((purse) => (
                <ScholarshipAwardCard
                  distributionStatus={data.status}
                  awardStatus={awardScholarshipMutation.status}
                  onAward={handleAwardPurse}
                  key={purse.id + purse.name}
                  purse={purse}
                  programId={programId}
                />
              ))}
            </div>
          </div>
        )
      }}
    </PageLoader>
  )
}
